import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Grid,
  MenuItem,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Skeleton,
  Chip,
  Menu,
  Checkbox
} from '@mui/material';
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

const formatUserName = (u) => {
  if (!u) return '—';
  const n = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return n || u.email || '—';
};

const formatActionLabel = (action) => {
  if (!action) return '—';
  return String(action)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const AllActivities = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [order, setOrder] = useState('desc');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    when: true,
    user: true,
    module: true,
    action: true,
    description: true,
    view: true
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const visibleColCount =
    (visibleCols.when ? 1 : 0) +
    (visibleCols.user ? 1 : 0) +
    (visibleCols.module ? 1 : 0) +
    (visibleCols.action ? 1 : 0) +
    (visibleCols.description ? 1 : 0) +
    (visibleCols.view ? 1 : 0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await api.get('/activities/user-filter-options');
      setUserOptions(r.data?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    p.set('page', String(page + 1));
    p.set('limit', String(rowsPerPage));
    p.set('order', order);
    if (debouncedSearch) p.set('search', debouncedSearch);
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    if (selectedUser?.id) p.set('user_id', selectedUser.id);
    return p;
  }, [page, rowsPerPage, order, debouncedSearch, startDate, endDate, selectedUser]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/activities?${buildQuery()}`);
      setRows(r.data?.data || []);
      setTotal(r.data?.pagination?.total || 0);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const clearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setSelectedUser(null);
    setOrder('desc');
    setPage(0);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const p = new URLSearchParams();
      p.set('order', order);
      if (debouncedSearch) p.set('search', debouncedSearch);
      if (startDate) p.set('start_date', startDate);
      if (endDate) p.set('end_date', endDate);
      if (selectedUser?.id) p.set('user_id', selectedUser.id);
      const r = await api.get(`/activities/company-log.pdf?${p.toString()}`, { responseType: 'blob' });
      const name =
        (startDate || endDate)
          ? `company-activity-log-${startDate || 'start'}-to-${endDate || 'end'}.pdf`
          : 'company-activity-log.pdf';
      const url = window.URL.createObjectURL(
        new Blob([r.data], { type: 'application/pdf' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = name.replace(/[^a-z0-9_.-]+/gi, '-');
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF download started');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const detailsString = useMemo(() => {
    if (!viewRow) return '';
    const d = viewRow.details;
    if (d == null) return '';
    try {
      return typeof d === 'string' ? d : JSON.stringify(d, null, 2);
    } catch {
      return String(d);
    }
  }, [viewRow]);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          All activities
        </Typography>
        <Button
          variant="outlined"
          startIcon={exporting ? null : <PdfIcon />}
          disabled={exporting}
          onClick={exportPdf}
        >
          {exporting ? 'Preparing…' : 'Company log (PDF)'}
        </Button>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        Read-only company activity stream (manual “Log activity” entries and automatic system events). This list cannot be edited or deleted. Export applies the current filters; PDF includes up to 3,000 matching rows.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1, fontSize: 20 }} /> }}
                placeholder="Description, entity name…"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={userOptions}
                getOptionLabel={(o) => o.name || o.email}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedUser}
                onChange={(_, v) => {
                  setSelectedUser(v);
                  setPage(0);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Performed by" placeholder="All users" />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                select
                label="Date sort"
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="desc">Newest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button onClick={clearFilters} size="small">
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon fontSize="small" />}
            onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
            type="button"
          >
            Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchorEl}
            open={Boolean(colMenuAnchorEl)}
            onClose={() => setColMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {[
              ['when', 'When'],
              ['user', 'User'],
              ['module', 'Module'],
              ['action', 'Action'],
              ['description', 'Description'],
              ['view', 'View']
            ].map(([key, label]) => (
              <MenuItem
                key={key}
                onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
                dense
              >
                <Checkbox size="small" checked={Boolean(visibleCols[key])} />
                <Typography variant="body2">{label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleCols.when && <TableCell sx={{ fontWeight: 600 }}>When</TableCell>}
                {visibleCols.user && <TableCell sx={{ fontWeight: 600 }}>User</TableCell>}
                {visibleCols.module && <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>}
                {visibleCols.action && <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>}
                {visibleCols.description && <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>}
                {visibleCols.view && <TableCell align="right" sx={{ fontWeight: 600 }}>View</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(6)].map((_, i) => (
                    <TableRow key={i}>
                      {visibleCols.when && <TableCell><Skeleton /></TableCell>}
                      {visibleCols.user && <TableCell><Skeleton /></TableCell>}
                      {visibleCols.module && <TableCell><Skeleton /></TableCell>}
                      {visibleCols.action && <TableCell><Skeleton /></TableCell>}
                      {visibleCols.description && <TableCell><Skeleton /></TableCell>}
                      {visibleCols.view && <TableCell><Skeleton /></TableCell>}
                    </TableRow>
                  ))
                : rows.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No activities for these filters</Typography>
                      </TableCell>
                    </TableRow>
                  )
                : rows.map((a) => (
                    <TableRow key={a.id} hover>
                      {visibleCols.when && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {a.performed_at
                          ? new Date(a.performed_at).toLocaleString()
                          : '—'}
                      </TableCell>}
                      {visibleCols.user && <TableCell>
                        {formatUserName(a.user)}
                        {a.user?.email && (
                          <Typography display="block" variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {a.user.email}
                          </Typography>
                        )}
                      </TableCell>}
                      {visibleCols.module && <TableCell>
                        <Chip size="small" label={a.module || '—'} sx={{ textTransform: 'capitalize' }} />
                      </TableCell>}
                      {visibleCols.action && <TableCell>{formatActionLabel(a.action)}</TableCell>}
                      {visibleCols.description && <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 420 }} noWrap title={a.description}>
                          {a.description || '—'}
                        </Typography>
                        {a.entity_name && (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {a.entity_name}
                          </Typography>
                        )}
                      </TableCell>}
                      {visibleCols.view && <TableCell align="right">
                        <IconButton size="small" aria-label="View" onClick={() => setViewRow(a)} color="primary">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </TableCell>}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="span" variant="h6">Activity (read-only)</Typography>
          <IconButton onClick={() => setViewRow(null)} aria-label="Close" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Box>
              {[
                ['Code', viewRow.activity_code || '—'],
                ['When', viewRow.performed_at ? new Date(viewRow.performed_at).toLocaleString() : '—'],
                ['User', formatUserName(viewRow.user) + (viewRow.user?.email ? `  (${viewRow.user.email})` : '')],
                ['Module', viewRow.module || '—'],
                ['Action', viewRow.action || '—'],
                ['Entity', viewRow.entity_type && viewRow.entity_id ? `${viewRow.entity_type} / ${viewRow.entity_id}` : (viewRow.entity_type || '—')],
                ['Entity name', viewRow.entity_name || '—'],
                ['Description', viewRow.description || '—'],
                ['IP', viewRow.ip_address || '—'],
              ].map(([k, v]) => (
                <Box key={k} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{v}</Typography>
                </Box>
              ))}
              {detailsString ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Details
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      fontSize: 12,
                      overflow: 'auto',
                      maxHeight: 280
                    }}
                  >
                    {detailsString}
                  </Box>
                </Box>
              ) : null}
              {viewRow.user_agent && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">User agent</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: 12 }}>
                    {viewRow.user_agent}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllActivities;
